'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import Toolbar from 'DesignEditorsUI/components/shared/Toolbar';
import SaveStatusLabel from 'DesignEditorsUI/components/shared/SaveStatusLabel';
import UndoRedo from 'DesignEditorsUI/components/shared/controls/UndoRedo';
import DisabledPublishTooltip from 'DesignEditorsUI/components/shared/DisabledPublishTooltip';
import PreviewerAppLink from 'DesignEditorsUI/components/shared/PreviewerAppLink';
import IsAvailableForNewContentToggle from 'DesignEditorsUI/components/shared/controls/IsAvailableForNewContentToggle';
import withConnectedToolbar from './decorators/withProjectsToolbar';
import EditorError from 'DesignData/models/validation/EditorError';
import { STAGED } from 'DesignData/enums/StagingEnvironments';

const i18nKey = key => `DesignEditorsUI.shared.toolbars.${key}`;

const toolbarI18nKey = key => `DesignEditorsUI.customWidgetEditor.toolbar.${key}`;

const errorsI18nkey = key => `DesignEditorsUI.customWidgetEditor.errors.savedWithErrors.${key}`;

class CustomWidgetToolbar extends PureComponent {
  constructor(...args) {
    super(...args);

    this.onIsAvailableForNewContentToggleChange = e => {
      const {
        checked
      } = e.target;
      this.props.onUpdateCustomWidget({
        isAvailableForNewContent: checked
      });
    };

    this.onIsEnabledForEmailV3RenderingToggleChange = isEnabledForEmailV3Rendering => {
      this.props.onUpdateCustomWidget({
        isEnabledForEmailV3Rendering
      });
    };

    this.isPublishDisabled = () => {
      const {
        isReadOnly,
        requestStatus,
        editorErrors,
        hasBufferedChanges,
        isBufferLoaded
      } = this.props;
      return !!(!isBufferLoaded || isReadOnly || requestStatus.isPublishing || requestStatus.isSaving || requestStatus.isValidating || requestStatus.saveError || editorErrors && editorErrors.length || !hasBufferedChanges);
    };

    this.renderDisabledPublishTooltip = (tooltipChild, placement) => {
      const {
        requestStatus,
        openDrawer,
        editorErrors,
        hasBufferedChanges
      } = this.props;
      return /*#__PURE__*/_jsx(DisabledPublishTooltip, {
        placement: placement,
        requestStatus: requestStatus,
        openDrawer: openDrawer,
        errors: editorErrors,
        hasBufferedChanges: hasBufferedChanges,
        children: tooltipChild
      });
    };

    this.renderLeftContent = () => {
      const {
        requestStatus,
        onRedoCustomWidgetEdit,
        onUndoCustomWidgetEdit,
        futureEditHistoryCount,
        pastEditHistoryCount,
        editorErrors,
        openDrawer
      } = this.props;

      const errorsLink = /*#__PURE__*/_jsx(UIButton, {
        onClick: openDrawer,
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: errorsI18nkey('errorsLinkLabel'),
          options: {
            count: editorErrors.length
          }
        })
      });

      const successWithErrorsMessage = /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: errorsI18nkey('message'),
        options: {
          errorsLink
        }
      });

      return /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        children: [/*#__PURE__*/_jsx("div", {
          className: "m-right-2",
          children: /*#__PURE__*/_jsx(UndoRedo, {
            futureEditCount: futureEditHistoryCount,
            pastEditCount: pastEditHistoryCount,
            redo: onRedoCustomWidgetEdit,
            undo: onUndoCustomWidgetEdit
          })
        }), /*#__PURE__*/_jsx(SaveStatusLabel, {
          requestStatus: requestStatus,
          successWithErrorsMessage: successWithErrorsMessage
        })]
      });
    };

    this.renderIsAvailableForNewContentToggle = () => {
      if (this.props.schemaVersion === 1) {
        return null;
      }

      return /*#__PURE__*/_jsx(IsAvailableForNewContentToggle, {
        disabled: !this.props.isBufferLoaded || this.props.isReadOnly,
        checked: !!this.props.isAvailableForNewContent,
        onChange: this.onIsAvailableForNewContentToggleChange,
        toggleLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: toolbarI18nKey('isAvailableForNewContentToggle.label')
        }),
        tooltipTitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: toolbarI18nKey('isAvailableForNewContentToggle.tooltipTitle')
        })
      });
    };

    this.renderPublishButton = () => {
      const {
        onPublish,
        isReadOnly
      } = this.props;
      const disabled = this.isPublishDisabled();

      const button = /*#__PURE__*/_jsx(UIButton, {
        disabled: disabled,
        onClick: onPublish,
        size: "extra-small",
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('buttons.publish')
        })
      });

      if (!disabled || isReadOnly) {
        return button;
      }

      return this.renderDisabledPublishTooltip(button);
    };

    this.renderStageChangesButton = () => {
      const disabled = this.isPublishDisabled();
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "stage-changes-button",
        disabled: disabled,
        onClick: this.props.onStageChanges,
        size: "extra-small",
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: i18nKey('buttons.stageChanges')
        })
      });
    };

    this.renderRightContent = () => {
      const {
        previewUrl,
        isProjectsUngated,
        isInProjectsFolder
      } = this.props;
      const stagingEnabled = isProjectsUngated && isInProjectsFolder;
      const previewModuleUrl = previewUrl && stagingEnabled ? `${previewUrl}?environmentId=${STAGED}` : previewUrl;
      return /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "end",
        children: [/*#__PURE__*/_jsx("div", {
          className: "m-right-3",
          children: this.renderIsAvailableForNewContentToggle()
        }), previewModuleUrl && /*#__PURE__*/_jsx(PreviewerAppLink, {
          url: previewModuleUrl
        }), stagingEnabled && this.renderStageChangesButton(), !isInProjectsFolder && this.renderPublishButton()]
      });
    };
  }

  render() {
    return /*#__PURE__*/_jsx(Toolbar, {
      disabled: !this.props.isBufferLoaded,
      leftContent: this.renderLeftContent(),
      rightContent: this.renderRightContent(),
      previewUrl: this.props.previewUrl
    });
  }

}

CustomWidgetToolbar.propTypes = {
  isBufferLoaded: PropTypes.bool.isRequired,
  isAvailableForNewContent: PropTypes.bool.isRequired,
  schemaVersion: PropTypes.number,
  futureEditHistoryCount: PropTypes.number.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  onPublish: PropTypes.func.isRequired,
  onStageChanges: PropTypes.func,
  onRedoCustomWidgetEdit: PropTypes.func.isRequired,
  onUndoCustomWidgetEdit: PropTypes.func.isRequired,
  pastEditHistoryCount: PropTypes.number.isRequired,
  previewUrl: PropTypes.string,
  requestStatus: PropTypes.object.isRequired,
  openDrawer: PropTypes.func.isRequired,
  editorErrors: PropTypes.arrayOf(PropTypes.instanceOf(EditorError)),
  hasBufferedChanges: PropTypes.bool,
  onUpdateCustomWidget: PropTypes.func.isRequired,
  isInProjectsFolder: PropTypes.bool.isRequired,
  isProjectsUngated: PropTypes.bool.isRequired
};
export default withConnectedToolbar(CustomWidgetToolbar);